import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import {
  COUNTRIES,
  GENDER_OPTIONS,
  BLOOD_TYPES,
  ACADEMIC_LEVEL_OPTIONS,
  getDocumentTypes,
  getStatesForCountry,
  getCitiesForState,
} from '@alascorporation/utils'
import { getMyStudentProfile, getStoredUser, updateMyStudentProfile, type StudentProfile } from '../../lib/api'

const emptyProfile: StudentProfile = {}

type SectionId = 'personal' | 'document' | 'location' | 'health' | 'guardian' | 'institution'

const SECTIONS: { id: SectionId; title: string; hint: string }[] = [
  { id: 'personal', title: 'Tus datos', hint: 'Nombre, correo, cumpleaños' },
  { id: 'document', title: 'Tu documento', hint: 'Cédula o tarjeta de identidad' },
  { id: 'location', title: 'Dónde vives', hint: 'Ciudad y dirección' },
  { id: 'health', title: 'Salud', hint: 'Tipo de sangre y lo que debamos saber' },
  { id: 'guardian', title: 'Tu acudiente', hint: 'A quién contactar si hay una emergencia' },
  { id: 'institution', title: 'Tu colegio', hint: 'Dónde estudias y en qué grado vas' },
]

const SECTION_ICONS: Record<SectionId, ReactNode> = {
  personal: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
    </svg>
  ),
  document: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="12" r="1.8" />
      <path d="M13 10h5M13 14h5" strokeLinecap="round" />
    </svg>
  ),
  location: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M12 21s-7-6.2-7-11a7 7 0 1114 0c0 4.8-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  ),
  health: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
      <path d="M12 20s-7.5-4.6-7.5-10A4.5 4.5 0 0112 6.5 4.5 4.5 0 0119.5 10c0 5.4-7.5 10-7.5 10z" />
    </svg>
  ),
  guardian: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="8.5" cy="9" r="2.6" />
      <circle cx="16" cy="10" r="2.1" />
      <path d="M3.5 19c.4-3 2.5-4.8 5-4.8s4.6 1.8 5 4.8M14.3 14.6c2 .2 3.5 1.8 3.8 4.4" />
    </svg>
  ),
  institution: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
      <path d="M12 4l9 4.5-9 4.5-9-4.5L12 4z" />
      <path d="M6 10.5V16c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5.5" />
    </svg>
  ),
}

function isFilled(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

const SECTION_FIELD_KEYS: Record<SectionId, (keyof StudentProfile)[]> = {
  personal: ['firstName', 'lastName', 'birthDate', 'gender'],
  document: ['countryCode', 'documentType', 'documentNumber'],
  location: ['residenceCountryCode', 'address', 'stateProvince', 'city'],
  health: ['bloodType', 'hasDisability', 'disabilityDetail', 'medications'],
  guardian: ['guardianName', 'guardianRelationship', 'guardianDocumentType', 'guardianDocumentNumber', 'guardianPhone', 'guardianEmail'],
  institution: ['institution', 'selfDeclaredRole', 'academicLevel'],
}

function isSectionDone(profile: StudentProfile, id: SectionId): boolean {
  return SECTION_FIELD_KEYS[id].every((key) => isFilled(profile[key]))
}

export function StudentProfileSection() {
  const storedUser = getStoredUser()
  const [profile, setProfile] = useState<StudentProfile>(emptyProfile)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [activeSection, setActiveSection] = useState<SectionId>('personal')

  useEffect(() => {
    let cancelled = false
    getMyStudentProfile()
      .then((result) => {
        if (!cancelled && result) setProfile({ ...emptyProfile, ...result })
      })
      .catch(() => {
        // Best-effort — the section still renders an empty, editable form.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  function set<K extends keyof StudentProfile>(key: K, value: StudentProfile[K]) {
    setProfile((current) => ({ ...current, [key]: value }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isSaving) return
    setError(null)
    setSuccess(false)
    setIsSaving(true)
    try {
      const updated = await updateMyStudentProfile(profile)
      setProfile({ ...emptyProfile, ...updated })
      setSuccess(true)
    } catch {
      setError('No se pudo actualizar tu información. Verifica los datos e intenta de nuevo.')
    } finally {
      setIsSaving(false)
    }
  }

  const documentTypes = getDocumentTypes(profile.countryCode)
  const residenceStates = getStatesForCountry(profile.residenceCountryCode)
  const selectedState = residenceStates.find((s) => s.value === profile.stateProvince)
  const residenceCities = getCitiesForState(profile.residenceCountryCode, selectedState?.isoCode)

  return (
    <section>
      <p className="account-section__eyebrow">SateLab</p>
      <h1 className="account-section__title">Tu información</h1>
      <p className="account-section__subtitle">
        Ve llenando cada parte a tu ritmo — nada se pierde. Escoge un tema de la lista para empezar.
      </p>

      <div className="account-section__body">
        {isLoading ? (
          <p className="account-status">Cargando información…</p>
        ) : (
          <form onSubmit={handleSubmit} className="account-profile-form">
            <nav className="account-section-nav">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`account-section-nav__item${activeSection === s.id ? ' account-section-nav__item--active' : ''}`}
                  onClick={() => setActiveSection(s.id)}
                >
                  <span className="account-section-nav__icon">{SECTION_ICONS[s.id]}</span>
                  <span className="account-section-nav__text">
                    <span className="account-section-nav__title">{s.title}</span>
                    <span className="account-section-nav__hint">{s.hint}</span>
                  </span>
                  {isSectionDone(profile, s.id) && (
                    <span className="account-section-nav__done" aria-label="Completo">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="account-section-panel">
              {activeSection === 'personal' && (
                <div className="account-field-grid">
                  <div className="account-field">
                    <label className="auth-label">Correo electrónico</label>
                    <input className="auth-input" value={storedUser?.email ?? ''} disabled />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Nombres</label>
                    <input className="auth-input" value={profile.firstName ?? ''} onChange={(e) => set('firstName', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Apellidos</label>
                    <input className="auth-input" value={profile.lastName ?? ''} onChange={(e) => set('lastName', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Fecha de nacimiento</label>
                    <input type="date" className="auth-input" value={profile.birthDate ?? ''} onChange={(e) => set('birthDate', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Género</label>
                    <select
                      className="auth-input"
                      value={profile.gender ?? ''}
                      onChange={(e) => set('gender', (e.target.value || null) as StudentProfile['gender'])}
                    >
                      <option value="">Prefiero no decir</option>
                      {GENDER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {activeSection === 'document' && (
                <div className="account-field-grid">
                  <div className="account-field">
                    <label className="auth-label">País del documento</label>
                    <select className="auth-input" value={profile.countryCode ?? ''} onChange={(e) => set('countryCode', e.target.value || null)}>
                      <option value="">Selecciona…</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Tipo de documento</label>
                    <select className="auth-input" value={profile.documentType ?? ''} onChange={(e) => set('documentType', e.target.value || null)}>
                      <option value="">Selecciona…</option>
                      {documentTypes.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Número de documento</label>
                    <input className="auth-input" value={profile.documentNumber ?? ''} onChange={(e) => set('documentNumber', e.target.value || null)} />
                  </div>
                </div>
              )}

              {activeSection === 'location' && (
                <div className="account-field-grid">
                  <div className="account-field">
                    <label className="auth-label">País de residencia</label>
                    <select
                      className="auth-input"
                      value={profile.residenceCountryCode ?? ''}
                      onChange={(e) => {
                        set('residenceCountryCode', e.target.value || null)
                        set('stateProvince', null)
                        set('city', null)
                      }}
                    >
                      <option value="">Selecciona…</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Dirección de residencia</label>
                    <input className="auth-input" value={profile.address ?? ''} onChange={(e) => set('address', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Departamento / Estado / Provincia</label>
                    {residenceStates.length > 0 ? (
                      <select
                        className="auth-input"
                        value={profile.stateProvince ?? ''}
                        onChange={(e) => {
                          set('stateProvince', e.target.value || null)
                          set('city', null)
                        }}
                      >
                        <option value="">Selecciona…</option>
                        {residenceStates.map((s) => (
                          <option key={s.isoCode} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="auth-input"
                        value={profile.stateProvince ?? ''}
                        onChange={(e) => set('stateProvince', e.target.value || null)}
                        disabled={!profile.residenceCountryCode}
                        placeholder={profile.residenceCountryCode ? '' : 'Selecciona primero el país'}
                      />
                    )}
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Municipio / Ciudad</label>
                    {residenceCities.length > 0 ? (
                      <select className="auth-input" value={profile.city ?? ''} onChange={(e) => set('city', e.target.value || null)}>
                        <option value="">Selecciona…</option>
                        {residenceCities.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="auth-input"
                        value={profile.city ?? ''}
                        onChange={(e) => set('city', e.target.value || null)}
                        disabled={residenceStates.length > 0 && !selectedState}
                        placeholder={residenceStates.length > 0 && !selectedState ? 'Selecciona primero el departamento' : ''}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'health' && (
                <div className="account-field-grid">
                  <div className="account-field">
                    <label className="auth-label">Grupo sanguíneo</label>
                    <select className="auth-input" value={profile.bloodType ?? ''} onChange={(e) => set('bloodType', e.target.value || null)}>
                      <option value="">Selecciona…</option>
                      {BLOOD_TYPES.map((bt) => (
                        <option key={bt} value={bt}>
                          {bt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="account-field">
                    <label className="auth-label">¿Tienes alguna discapacidad?</label>
                    <select
                      className="auth-input"
                      value={profile.hasDisability === null || profile.hasDisability === undefined ? '' : String(profile.hasDisability)}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : e.target.value === 'true'
                        set('hasDisability', value)
                        if (!value) set('disabilityDetail', null)
                      }}
                    >
                      <option value="">Prefiero no decir</option>
                      <option value="false">No</option>
                      <option value="true">Sí</option>
                    </select>
                  </div>
                  {profile.hasDisability && (
                    <div className="account-field">
                      <label className="auth-label">¿Cuál es tu discapacidad?</label>
                      <input className="auth-input" value={profile.disabilityDetail ?? ''} onChange={(e) => set('disabilityDetail', e.target.value || null)} />
                    </div>
                  )}
                  <div className="account-field">
                    <label className="auth-label">Si tomas algún medicamento, escríbelo aquí</label>
                    <input className="auth-input" value={profile.medications ?? ''} onChange={(e) => set('medications', e.target.value || null)} />
                  </div>
                </div>
              )}

              {activeSection === 'guardian' && (
                <div className="account-field-grid">
                  <div className="account-field">
                    <label className="auth-label">Nombre completo</label>
                    <input className="auth-input" value={profile.guardianName ?? ''} onChange={(e) => set('guardianName', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Parentesco</label>
                    <input className="auth-input" value={profile.guardianRelationship ?? ''} onChange={(e) => set('guardianRelationship', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Tipo de documento</label>
                    <select
                      className="auth-input"
                      value={profile.guardianDocumentType ?? ''}
                      onChange={(e) => set('guardianDocumentType', e.target.value || null)}
                    >
                      <option value="">Selecciona…</option>
                      {documentTypes.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Número de documento</label>
                    <input
                      className="auth-input"
                      value={profile.guardianDocumentNumber ?? ''}
                      onChange={(e) => set('guardianDocumentNumber', e.target.value || null)}
                    />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Número de celular</label>
                    <input className="auth-input" value={profile.guardianPhone ?? ''} onChange={(e) => set('guardianPhone', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Correo electrónico</label>
                    <input
                      type="email"
                      className="auth-input"
                      value={profile.guardianEmail ?? ''}
                      onChange={(e) => set('guardianEmail', e.target.value || null)}
                    />
                  </div>
                </div>
              )}

              {activeSection === 'institution' && (
                <div className="account-field-grid">
                  <div className="account-field">
                    <label className="auth-label">Institución educativa</label>
                    <input className="auth-input" value={profile.institution ?? ''} onChange={(e) => set('institution', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Rol en el equipo</label>
                    <input className="auth-input" value={profile.selfDeclaredRole ?? ''} onChange={(e) => set('selfDeclaredRole', e.target.value || null)} />
                  </div>
                  <div className="account-field">
                    <label className="auth-label">Nivel de formación académica</label>
                    <select className="auth-input" value={profile.academicLevel ?? ''} onChange={(e) => set('academicLevel', e.target.value || null)}>
                      <option value="">Selecciona…</option>
                      {ACADEMIC_LEVEL_OPTIONS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {error && (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              )}
              {success && (
                <p className="account-success" role="status">
                  La información se actualizó correctamente.
                </p>
              )}

              <button type="submit" className="auth-button" disabled={isSaving} style={{ marginTop: '1.25rem' }}>
                {isSaving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  )
}
